import {
  TileDef,
  SiteDef,
  ToolType,
  LayerDef,
  MaterialType,
  ArtifactInstance,
  Condition,
  TOOLS,
  GAME_CONFIG,
} from '../types';
import { ARTIFACT_POOLS } from '../data/artifacts';
import { RNG } from '../utils/RNG';
import { uid } from '../utils/helpers';

export interface DigResult {
  tilesAffected: { x: number; y: number }[];
  artifactFound: ArtifactInstance | null;
  timeSpent: number;
  layerRevealed: boolean;
  fullyExcavated: boolean;
}

const MATERIAL_HARDNESS: Record<MaterialType, number> = {
  topsoil: 1,
  clay: 3,
  sediment: 2,
  rock: 5,
  bedrock: 8,
  sand: 1,
  permafrost: 6,
  water: 4,
  volcanic: 7,
};

export class DigSite {
  siteDef: SiteDef;
  grid: TileDef[][];
  artifacts: ArtifactInstance[] = [];
  currentDay: number = 1;
  timeUsedToday: number = 0;
  maxTimePerDay: number = 8;
  private rng: RNG;

  constructor(siteDef: SiteDef, rng: RNG) {
    this.siteDef = siteDef;
    this.rng = rng;
    this.grid = [];
    this.initGrid();
    this.placeArtifacts();
  }

  /** Build gridSize x gridSize grid. Each tile gets layers from siteDef.layerTypes. */
  private initGrid(): void {
    const size = this.siteDef.gridSize;
    this.grid = [];

    for (let y = 0; y < size; y++) {
      const row: TileDef[] = [];
      for (let x = 0; x < size; x++) {
        const layers: LayerDef[] = this.siteDef.layerTypes.map((mat, depth) => ({
          material: mat,
          hardness: MATERIAL_HARDNESS[mat],
          depth,
        }));

        row.push({
          layers,
          currentLayer: 0,
          artifactId: null,
          artifactLayer: -1,
          revealed: false,
          marked: false,
          scanned: false,
        });
      }
      this.grid.push(row);
    }
  }

  /** Pick 60-80% of the site's artifact pool and place at random positions / layers. */
  private placeArtifacts(): void {
    const pool = ARTIFACT_POOLS[this.siteDef.id];
    if (!pool || pool.length === 0) return;

    const placePct = this.rng.float(0.6, 0.8);
    const count = Math.max(1, Math.round(pool.length * placePct));
    const shuffled = this.rng.shuffle(pool);
    const toPlace = shuffled.slice(0, count);
    const size = this.siteDef.gridSize;
    const maxLayers = this.siteDef.layerTypes.length;

    for (const def of toPlace) {
      // Find an unoccupied tile
      let attempts = 0;
      let x: number;
      let y: number;
      do {
        x = this.rng.range(0, size - 1);
        y = this.rng.range(0, size - 1);
        attempts++;
      } while (this.grid[y][x].artifactId !== null && attempts < size * size * 2);

      if (this.grid[y][x].artifactId !== null) continue; // couldn't find a spot

      // Pick a layer -- prefer deeper layers, never top layer
      const minLayer = 1;
      const maxLayer = maxLayers - 1;
      const layer = this.rng.range(minLayer, maxLayer);

      const instance: ArtifactInstance = {
        defId: def.id,
        condition: 'pristine', // determined when excavated
        analysisState: 'dirty',
        analysisProgress: 0,
        discovered: false,
        siteId: this.siteDef.id,
        gridX: x,
        gridY: y,
        sold: false,
        displayed: false,
        displaySlot: -1,
        uid: uid(),
      };

      this.grid[y][x].artifactId = instance.uid;
      this.grid[y][x].artifactLayer = layer;
      this.artifacts.push(instance);
    }
  }

  /** Dig one tile's current layer with the given tool. Returns null if invalid. */
  dig(
    x: number,
    y: number,
    tool: ToolType,
    weatherMultiplier: number,
    skillSpeedMod: number,
    skillDamageMod: number,
  ): DigResult | null {
    if (!this.isValidTile(x, y)) return null;
    const tile = this.grid[y][x];
    if (this.isTileComplete(x, y)) return null;

    const layer = tile.layers[tile.currentLayer];
    const toolDef = TOOLS[tool];

    // Permafrost restriction: only pickaxe or charges can dig permafrost
    if (
      this.siteDef.specialMechanic === 'permafrost' &&
      layer.material === 'permafrost' &&
      tool !== 'pickaxe' &&
      tool !== 'charges'
    ) {
      return null;
    }

    // Calculate time cost
    let time = toolDef.speed * layer.hardness * weatherMultiplier * skillSpeedMod;

    // Special mechanic multipliers
    if (this.siteDef.specialMechanic === 'water_table' && layer.material === 'water') {
      time *= 2;
    }
    if (this.siteDef.specialMechanic === 'permafrost' && layer.material === 'permafrost') {
      time *= 2;
    }
    if (this.siteDef.specialMechanic === 'underwater') {
      time *= 1.5;
    }

    // Allow at least one dig per day even if it would exceed the budget
    if (this.timeUsedToday > 0 && this.timeUsedToday + time > this.maxTimePerDay) return null;
    this.timeUsedToday += time;

    // Check for artifact at this layer
    let artifactFound: ArtifactInstance | null = null;
    if (tile.artifactId !== null && tile.artifactLayer === tile.currentLayer) {
      const instance = this.artifacts.find((a) => a.uid === tile.artifactId);
      if (instance && !instance.discovered) {
        instance.discovered = true;
        instance.condition = this.determineCondition(toolDef, skillDamageMod, instance);
        artifactFound = instance;
        // Clear tile artifact reference so the sparkle icon stops rendering
        tile.artifactId = null;
      }
    }

    // Advance layer
    tile.currentLayer++;
    const layerRevealed = tile.currentLayer < tile.layers.length;
    const fullyExcavated = this.isTileComplete(x, y);
    tile.revealed = true;

    return {
      tilesAffected: [{ x, y }],
      artifactFound,
      timeSpent: time,
      layerRevealed,
      fullyExcavated,
    };
  }

  /** Scan with GPR -- reveals artifact presence within radius (Manhattan distance). */
  scan(
    x: number,
    y: number,
    radius: number,
  ): { x: number; y: number; hasArtifact: boolean }[] {
    const results: { x: number; y: number; hasArtifact: boolean }[] = [];
    const size = this.siteDef.gridSize;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.abs(dx) + Math.abs(dy) > radius) continue;
        const tx = x + dx;
        const ty = y + dy;
        if (tx < 0 || ty < 0 || tx >= size || ty >= size) continue;

        const tile = this.grid[ty][tx];
        tile.scanned = true;
        results.push({
          x: tx,
          y: ty,
          hasArtifact: tile.artifactId !== null,
        });
      }
    }

    return results;
  }

  /** Use excavation charges -- dig 3x3 area centered on (centerX, centerY). */
  useCharges(centerX: number, centerY: number, skillDamageMod: number): DigResult {
    const tilesAffected: { x: number; y: number }[] = [];
    let artifactFound: ArtifactInstance | null = null;
    let totalTime = 0;
    let anyLayerRevealed = false;
    let anyFullyExcavated = false;
    const toolDef = TOOLS.charges;
    const size = this.siteDef.gridSize;

    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const tx = centerX + dx;
        const ty = centerY + dy;
        if (tx < 0 || ty < 0 || tx >= size || ty >= size) continue;

        const tile = this.grid[ty][tx];
        if (this.isTileComplete(tx, ty)) continue;

        const layer = tile.layers[tile.currentLayer];
        const time = toolDef.speed * layer.hardness;
        totalTime += time;

        // Check for artifact
        if (tile.artifactId !== null && tile.artifactLayer === tile.currentLayer) {
          const instance = this.artifacts.find((a) => a.uid === tile.artifactId);
          if (instance && !instance.discovered) {
            instance.discovered = true;
            instance.condition = this.determineCondition(toolDef, skillDamageMod, instance);
            tile.artifactId = null;
            // Only report the first artifact found (most important one)
            if (!artifactFound) {
              artifactFound = instance;
            }
          }
        }

        tile.currentLayer++;
        tile.revealed = true;
        tilesAffected.push({ x: tx, y: ty });

        if (tile.currentLayer < tile.layers.length) anyLayerRevealed = true;
        if (this.isTileComplete(tx, ty)) anyFullyExcavated = true;
      }
    }

    this.timeUsedToday += totalTime;

    return {
      tilesAffected,
      artifactFound,
      timeSpent: totalTime,
      layerRevealed: anyLayerRevealed,
      fullyExcavated: anyFullyExcavated,
    };
  }

  /** Toggle the mark flag on a tile (for crew auto-dig). */
  toggleMark(x: number, y: number): void {
    if (!this.isValidTile(x, y)) return;
    this.grid[y][x].marked = !this.grid[y][x].marked;
  }

  /** Crew auto-dig: dig up to `count` marked tiles with trowel. */
  crewDig(
    count: number,
    weatherMultiplier: number,
    skillSpeedMod: number,
    skillDamageMod: number,
  ): DigResult[] {
    const results: DigResult[] = [];
    const size = this.siteDef.gridSize;
    let remaining = count;

    for (let y = 0; y < size && remaining > 0; y++) {
      for (let x = 0; x < size && remaining > 0; x++) {
        const tile = this.grid[y][x];
        if (!tile.marked || this.isTileComplete(x, y)) continue;
        if (!this.hasTimeLeft()) break;

        const result = this.dig(x, y, 'trowel', weatherMultiplier, skillSpeedMod, skillDamageMod);
        if (result) {
          results.push(result);
          remaining--;
        }
      }
    }

    return results;
  }

  /** Advance to the next day, resetting daily time. */
  advanceDay(): void {
    this.currentDay++;
    this.timeUsedToday = 0;
  }

  /** Check whether a tile has been fully excavated (all layers removed). */
  isTileComplete(x: number, y: number): boolean {
    if (!this.isValidTile(x, y)) return false;
    const tile = this.grid[y][x];
    return tile.currentLayer >= tile.layers.length;
  }

  /** Get overall completion percentage of the grid. */
  getCompletionPercent(): number {
    const size = this.siteDef.gridSize;
    let totalLayers = 0;
    let completedLayers = 0;

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const tile = this.grid[y][x];
        totalLayers += tile.layers.length;
        completedLayers += tile.currentLayer;
      }
    }

    return totalLayers === 0 ? 100 : (completedLayers / totalLayers) * 100;
  }

  /** Can we still dig today? */
  hasTimeLeft(): boolean {
    return this.timeUsedToday < this.maxTimePerDay;
  }

  /** Get remaining days in the season. */
  getRemainingDays(): number {
    return Math.max(0, this.siteDef.seasonDays - this.currentDay);
  }

  /** Is the excavation season over? */
  isSeasonOver(): boolean {
    return this.currentDay > this.siteDef.seasonDays;
  }

  // ── Private helpers ──

  private isValidTile(x: number, y: number): boolean {
    const size = this.siteDef.gridSize;
    return x >= 0 && y >= 0 && x < size && y < size;
  }

  /**
   * Determine artifact condition based on tool damage chance, skill modifier,
   * and whether the artifact is fragile.
   */
  private determineCondition(
    toolDef: { damageCh: number },
    skillDamageMod: number,
    instance: ArtifactInstance,
  ): Condition {
    const pool = ARTIFACT_POOLS[instance.siteId];
    const def = pool?.find((a) => a.id === instance.defId);
    const fragile = def?.fragile ?? false;

    const effectiveDamageCh = toolDef.damageCh * skillDamageMod * (fragile ? 1.5 : 1.0);
    const damageRoll = this.rng.next();

    if (damageRoll < effectiveDamageCh) {
      // Tool damaged the artifact
      const outcomeRoll = this.rng.next();
      if (outcomeRoll < 0.5) return 'damaged';
      if (outcomeRoll < 0.8) return 'fair';
      return 'fragments';
    }

    // No damage from tool
    const outcomeRoll = this.rng.next();
    if (outcomeRoll < 0.4) return 'pristine';
    if (outcomeRoll < 0.8) return 'good';
    return 'fair';
  }
}
